import { logger } from "../utils/logger/logger";
import {
  VerificationStep,
  VerificationResponse,
  MockStep,
  SubSegment,
  ErrorId,
  Segment,
  SuccessResponse,
} from "../types/types";
import { MockStepsEnum, SegmentToStepMap, VerificationStepsEnum } from "../types/runtimeTypes";
import { getOptions } from "../../options/options";
import {
  QUERY_STRING_STEP_OVERRIDE,
  QUERY_STRING_SUBSEGMENT_OVERRIDE,
  QUERY_STRING_ERRORID_OVERRIDE,
  QUERY_STRING_REWARD_CODE_OVERRIDE,
  QUERY_STRING_PREV_STEP_OVERRIDE,
  QUERY_STRING_SEGMENT_OVERRIDE,
  QUERY_STRING_REDIRECT_URL_OVERRIDE,
} from "../../constants";

import { allMockedResponses } from "../../serverMocks/mocks";
import { deepClone, getSafe } from "../utils/objects";

// To load a specific step directly, visit e.g. localhost:1234/?mockStep=success
export const getRouteOverride = (
  response: VerificationResponse = undefined,
): MockStep | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    let step;
    const segment = getSafe(() => response.segment);

    if (parsed.get(QUERY_STRING_STEP_OVERRIDE)) {
      step = parsed.get(QUERY_STRING_STEP_OVERRIDE) as VerificationStep;
    } else if (getOptions().mockStep) {
      step = getOptions().mockStep;
    }
    if (step === MockStepsEnum.collect) {
      return SegmentToStepMap[segment];
    }
    return step;
  } catch (e) {
    logger.error(e, "getRouteOverride");
  }
};

// When loading a specific step, specify a segment
const getSegmentOverride = (): Segment | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_SEGMENT_OVERRIDE)) {
      return parsed.get(QUERY_STRING_SEGMENT_OVERRIDE) as Segment;
    }
    if (getOptions().mockSegment) {
      return getOptions().mockSegment;
    }
  } catch (e) {
    logger.error(e, "getSegmentOverride");
  }
};

// When loading a specific step, specify a subsegment
const getSubsegmentOverride = (): SubSegment | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_SUBSEGMENT_OVERRIDE)) {
      return parsed.get(QUERY_STRING_SUBSEGMENT_OVERRIDE) as SubSegment;
    }
    if (getOptions().mockSubSegment) {
      return getOptions().mockSubSegment;
    }
  } catch (e) {
    logger.error(e, "getSubsegmentOverride");
  }
};

// When loading the error step, specify an errorId
const getErrorIdOverride = (): ErrorId | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_ERRORID_OVERRIDE)) {
      return parsed.get(QUERY_STRING_ERRORID_OVERRIDE) as ErrorId;
    }
    if (getOptions().mockErrorId) {
      return getOptions().mockErrorId;
    }
  } catch (e) {
    logger.error(e, "getErrorIdOverride");
  }
};

// When loading the error step, specify an errorId
export const getPreviousStepOverride = (): VerificationStep | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_PREV_STEP_OVERRIDE)) {
      return parsed.get(QUERY_STRING_PREV_STEP_OVERRIDE) as VerificationStep;
    }
    if (getOptions()[QUERY_STRING_PREV_STEP_OVERRIDE]) {
      return getOptions()[QUERY_STRING_PREV_STEP_OVERRIDE];
    }
  } catch (e) {
    logger.error(e, QUERY_STRING_PREV_STEP_OVERRIDE);
  }
};

// Special case. The loading screen is not a step, so mocking it is a little different.
export const isMockingLoading = (): boolean => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);
    return (
      parsed.get(QUERY_STRING_STEP_OVERRIDE) === MockStepsEnum.loading ||
      getOptions()[QUERY_STRING_STEP_OVERRIDE] === MockStepsEnum.loading
    );
  } catch (e) {
    logger.error(e, "isMockingLoading");
    return false;
  }
};

// Special case when mocking loading. To get text to show up, specify a previous step
export const getMockedLoadingStep = (): VerificationStep => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);
    return (
      parsed.get(QUERY_STRING_PREV_STEP_OVERRIDE) || getOptions()[QUERY_STRING_PREV_STEP_OVERRIDE]
    );
  } catch (e) {
    return undefined;
  }
};

// When loading the success step, specify a reward code
const getRewardCodeOverride = (): string | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_REWARD_CODE_OVERRIDE)) {
      return parsed.get(QUERY_STRING_REWARD_CODE_OVERRIDE);
    }
    if (getOptions().mockRewardCode) {
      return getOptions().mockRewardCode;
    }
  } catch (e) {
    logger.error(e, "getRewardCodeOverride");
  }
  return undefined;
};

// When loading the success step, specify a redirect URL
const getRedirectUrlOverride = (): string | undefined => {
  try {
    const parsed: URLSearchParams = new URLSearchParams(window.location.search);

    if (parsed.get(QUERY_STRING_REDIRECT_URL_OVERRIDE)) {
      return parsed.get(QUERY_STRING_REDIRECT_URL_OVERRIDE);
    }
    if (getOptions().mockRedirectUrl) {
      return getOptions().mockRedirectUrl;
    }
  } catch (e) {
    logger.error(e, "getRedirectUrlOverride");
  }
  return undefined;
};

const getErrorStepMockResponseOverrides = (mockResponse: VerificationResponse) => {
  const mockResponseClone = deepClone(mockResponse);
  const errorMock = getErrorIdOverride();
  if (errorMock) {
    mockResponseClone.errorIds = [errorMock];
  }
  return mockResponseClone;
};

const getSuccessStepMockResponseOverrides = (mockResponse: VerificationResponse) => {
  const mockResponseClone = deepClone(mockResponse) as SuccessResponse;
  const rewardCodeMock = getRewardCodeOverride();
  const redirectUrlMock = getRedirectUrlOverride();

  if (rewardCodeMock) {
    mockResponseClone.rewardCode = rewardCodeMock;
  }

  if (redirectUrlMock) {
    mockResponseClone.redirectUrl = redirectUrlMock;
  }
  return mockResponseClone;
};

/**
 * Based on page request query params, return a fake response to allow direct access to any step.
 *
 * @param overriddenStep
 * @param serverResponse The actual serverResponse to acquire certain pieces of info from.
 */
export const getOverriddenMock = async (
  overriddenStep: MockStep,
  serverResponse?: VerificationResponse,
): Promise<VerificationResponse> => {
  let mockResponse = getOptions().mockResponse || allMockedResponses[overriddenStep];

  if (overriddenStep && mockResponse) {
    logger.info(`getOverriddenMock: mocking ${overriddenStep} response`);

    switch (overriddenStep) {
      case VerificationStepsEnum.error:
        mockResponse = getErrorStepMockResponseOverrides(mockResponse);
        break;

      case VerificationStepsEnum.success:
        mockResponse = getSuccessStepMockResponseOverrides(mockResponse);
        break;

      default:
        break;
    }
  } else {
    mockResponse = null;
    logger.info(`
            getOverriddenMock: not mocking response.
            You may mock a step response by adding things like ?mockStep=success to your route.
            ${overriddenStep}
        `);
  }

  if (mockResponse !== null && serverResponse) {
    await serverResponse;
    mockResponse.segment = serverResponse.segment;
    mockResponse.subSegment = serverResponse.subSegment;
    if (getSegmentOverride()) {
      mockResponse.segment = getSegmentOverride();
      logger.info(`getOverriddenMock: mocking segment '${mockResponse.segment}'`);
    }
    if (getSubsegmentOverride()) {
      mockResponse.subSegment = getSubsegmentOverride();
      logger.info(`getOverriddenMock: mocking subSegment '${mockResponse.subSegment}'`);
    }
  }
  return Promise.resolve(mockResponse);
};
